import React, { useEffect, useRef, useState } from "react";

const MazePage = () => {
  const svgRef = useRef();
  const WIDTH = 600;
  const HEIGHT = 400;

  // 1 = wall, 0 = open
  const map = [
    [1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
    [1, 0, 0, 0, 0, 0, 0, 0, 0, 1],
    [1, 0, 1, 1, 1, 0, 1, 1, 0, 1],
    [1, 0, 1, 1, 1, 0, 1, 1, 0, 1],
    [1, 0, 1, 0, 0, 0, 1, 1, 0, 1],
    [1, 0, 1, 0, 1, 1, 1, 1, 0, 1],
    [1, 0, 1, 0, 1, 1, 0, 0, 0, 1],
    [1, 0, 1, 0, 1, 1, 0, 1, 0, 1],
    [1, 0, 0, 0, 0, 0, 0, 0, 0, 1],
    [1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  ];

  const [player, setPlayer] = useState({
    x: 2,
    y: 2,
    dir: 0, // 0 = north, 1 = east, 2 = south, 3 = west
  });

  const directionVector = [
    [0, -1], // North
    [1, 0],  // East
    [0, 1],  // South
    [-1, 0], // West
  ];

  const rotateLeft = (dir) => (dir + 3) % 4;
  const rotateRight = (dir) => (dir + 1) % 4;

  const movePlayer = (action) => {
    setPlayer((prev) => {
      let { x, y, dir } = prev;

      if (action === "left") {
        dir = rotateLeft(dir);
      } else if (action === "right") {
        dir = rotateRight(dir);
      } else {
        const moveDir = action === "forward" ? dir : (dir + 2) % 4;
        const [dx, dy] = directionVector[moveDir];
        const nx = x + dx;
        const ny = y + dy;

        if (map[ny]?.[nx] === 0) {
          x = nx;
          y = ny;
        }
      }

      return { x, y, dir };
    });
  };

  // KEY HANDLER
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowLeft") movePlayer("left");
      else if (e.key === "ArrowRight") movePlayer("right");
      else if (e.key === "ArrowUp") movePlayer("forward");
      else if (e.key === "ArrowDown") movePlayer("backward");
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  // MINIMAP — reflects centralized position and direction
const renderMiniMap = () => {
  const scale = 30; // Tile size
  const playerRadius = 7;
  const arrowLength = 14; // Medium size
  const arrowWidth = 8;
  const items = [];

  for (let y = 0; y < map.length; y++) {
    for (let x = 0; x < map[0].length; x++) {
      items.push(
        <rect
          key={`tile-${x}-${y}`}
          x={x * scale}
          y={y * scale}
          width={scale}
          height={scale}
          fill={map[y][x] === 1 ? "#888" : "#000"}
          stroke="#333"
        />
      );
    }
  }

  const cx = player.x * scale + scale / 2;
  const cy = player.y * scale + scale / 2;

  const [dx, dy] = directionVector[player.dir];
  const leftVec = [-dy, dx];
  const rightVec = [dy, -dx];

  const tipX = cx + dx * arrowLength;
  const tipY = cy + dy * arrowLength;
  const leftX = cx + leftVec[0] * arrowWidth;
  const leftY = cy + leftVec[1] * arrowWidth;
  const rightX = cx + rightVec[0] * arrowWidth;
  const rightY = cy + rightVec[1] * arrowWidth;

// White directional triangle (arrow)
items.push(
  <polygon
    key="player-dir"
    points={`${tipX},${tipY} ${leftX},${leftY} ${rightX},${rightY}`}
    fill="red"
    stroke="black"
    strokeWidth="1"
  />
);

// Green player circle
items.push(
  <circle key="player" cx={cx} cy={cy} r={playerRadius} fill="limegreen" />
);


  return (
    <svg
      width={map[0].length * scale}
      height={map.length * scale}
      style={{
        position: "absolute",
        top: 10,
        left: 10,
        background: "black",
        border: "2px solid white",
      }}
    >
      {items}
    </svg>
  );
};




  // PLACEHOLDER: simple forward wall rendering
  const renderMainViewPRIOR = () => {
    const svg = svgRef.current;
    while (svg.firstChild) svg.removeChild(svg.firstChild);

    const [dx, dy] = directionVector[player.dir];

    for (let depth = 1; depth <= 3; depth++) {
      const tx = player.x + dx * depth;
      const ty = player.y + dy * depth;

      const cell = map[ty]?.[tx];

      const scale = 1 / depth;
      const w = 300 * scale;
      const h = 300 * scale;

      const cx = WIDTH / 2;
      const cy = HEIGHT / 2;
      const x1 = cx - w / 2;
      const x2 = cx + w / 2;
      const y1 = cy - h / 2;
      const y2 = cy + h / 2;

      if (cell === 1) {
        drawLine(svg, x1, y1, x2, y1);
        drawLine(svg, x2, y1, x2, y2);
        drawLine(svg, x2, y2, x1, y2);
        drawLine(svg, x1, y2, x1, y1);
        break;
      } else {
        drawLine(svg, x1, y1, x2, y1);
        drawLine(svg, x1, y2, x2, y2);
      }
    }
  };

  const drawLine = (svg, x1, y1, x2, y2, color = "white") => {
    const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
    line.setAttribute("x1", x1);
    line.setAttribute("y1", y1);
    line.setAttribute("x2", x2);
    line.setAttribute("y2", y2);
    line.setAttribute("stroke", color);
    svg.appendChild(line);
  };

const renderMainView = () => {
  const svg = svgRef.current;
  while (svg.firstChild) svg.removeChild(svg.firstChild);

  const [dx, dy] = directionVector[player.dir];
  let prev = null;

  for (let depth = 1; depth <= 3; depth++) {
    const tx = player.x + dx * depth;
    const ty = player.y + dy * depth;
    const cell = map[ty]?.[tx];

    const scale = 1 / depth;
    const w = 300 * scale;
    const h = 300 * scale;

    const cx = WIDTH / 2;
    const cy = HEIGHT / 2;
    const x1 = cx - w / 2;
    const x2 = cx + w / 2;
    const y1 = cy - h / 2;
    const y2 = cy + h / 2;

    // Store corners for diagonal drawing
    const current = {
      topLeft: [x1, y1],
      topRight: [x2, y1],
      bottomLeft: [x1, y2],
      bottomRight: [x2, y2],
    };

    // Draw floor and ceiling lines
    drawLine(svg, x1, y1, x2, y1, "#555"); // Ceiling
    drawLine(svg, x1, y2, x2, y2, "#555"); // Floor

    // Draw diagonals from previous depth to current
    if (prev) {
      drawLine(svg, prev.topLeft[0], prev.topLeft[1], current.topLeft[0], current.topLeft[1], "#888");     // ceiling left
      drawLine(svg, prev.topRight[0], prev.topRight[1], current.topRight[0], current.topRight[1], "#888"); // ceiling right
      drawLine(svg, prev.bottomLeft[0], prev.bottomLeft[1], current.bottomLeft[0], current.bottomLeft[1], "#aaa"); // floor left
      drawLine(svg, prev.bottomRight[0], prev.bottomRight[1], current.bottomRight[0], current.bottomRight[1], "#aaa"); // floor right
    }

    // Stop if we hit a wall
    if (cell === 1) {
      drawLine(svg, x1, y1, x2, y1, "#aaa"); // Top of wall
      drawLine(svg, x2, y1, x2, y2, "#aaa"); // Right wall
      drawLine(svg, x2, y2, x1, y2, "#aaa"); // Bottom
      drawLine(svg, x1, y2, x1, y1, "#aaa"); // Left wall


      // Check if next cell in same direction is also a wall
      const nextX = tx + dx;
      const nextY = ty + dy;
      const nextCell = map[nextY]?.[nextX];

      if (nextCell === 1) {
        // TODO: These are not getting drawn. BUT THE CORNERS are.
        // Internal vertical line where two wall segments meet
        drawLine(svg, x2, y1, x2, y2, "red");
      }

      break;
    }

    // Compute perpendicular direction for proper left/right checks
    const perp = [-dy, dx];
    const leftX = tx + perp[0];
    const leftY = ty + perp[1];
    const rightX = tx - perp[0];
    const rightY = ty - perp[1];

    const sideLeft = map[leftY]?.[leftX];
    const sideRight = map[rightY]?.[rightX];

    if (cell === 0) {
      if (sideLeft === 1) {
        drawLine(svg, x1, y1, x1, y2, "white"); // Left wall edge
      }
      if (sideRight === 1) {
        drawLine(svg, x2, y1, x2, y2, "white"); // Right wall edge
      }
    }

    prev = current;
  }
};

  useEffect(() => {
    renderMainView();
  }, [player]);

  return (
    <div
      style={{
        background: "black",
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
      }}
    >
      <svg ref={svgRef} width={WIDTH} height={HEIGHT} />
      {renderMiniMap()}
    </div>
  );
};

export default MazePage;

